import { fetchApi } from './client';
import { createCommentsApi } from '@overlayqa/shared';
import type {
  IssueComment,
  CreateCommentRequest,
  UpdateCommentRequest,
} from '@overlayqa/shared';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

// Dashboard fetchApi requires explicit token threading.
// Build a token-injecting adapter for each call.
function withToken(token: string) {
  return createCommentsApi((<T>(path: string, opts?: Record<string, unknown>) =>
    // A read that gates the comments section takes the screen-read deadline; writes stay unbounded.
    fetchApi<T>(path, { timeoutMs: opts?.method ? MUTATION_TIMEOUT_MS : SCREEN_READ_TIMEOUT_MS, ...opts, token })) as Parameters<typeof createCommentsApi>[0]);
}

// Note: fetchApi prepends /api internally and already unwraps the { data } envelope.
// So pass paths WITHOUT the /api prefix and expect fetchApi to return T directly.
export const commentsApi = {
  clientEmail(token: string, issueId: string) { return withToken(token).clientEmail(issueId); },
  retryClientEmail(token: string, issueId: string, batchId: string) { return withToken(token).retryClientEmail(issueId, batchId); },
  attachment(token: string, issueId: string, commentId: string, attachmentId: string) {
    return withToken(token).attachment(issueId, commentId, attachmentId);
  },
  list(token: string, issueId: string): Promise<IssueComment[]> {
    return withToken(token).list(issueId);
  },

  create(
    token: string,
    issueId: string,
    body: CreateCommentRequest,
    opts?: { idempotencyKey?: string },
  ): Promise<IssueComment> {
    return withToken(token).create(issueId, body, opts);
  },

  update(
    token: string,
    issueId: string,
    commentId: string,
    body: UpdateCommentRequest
  ): Promise<IssueComment> {
    return withToken(token).update(issueId, commentId, body);
  },

  async delete(token: string, issueId: string, commentId: string): Promise<void> {
    return withToken(token).delete(issueId, commentId);
  },
};
